import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Droplets, TrendingDown, TrendingUp, Minus } from "lucide-react";

export function useDrought() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/drought").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 3600_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

// The official USDM GA infographic is 1056×816; the state map occupies roughly
// x:[21,639] y:[114,775]. We crop to just the map via an aspect-locked window.
const MAP_W = 618, MAP_H = 661, OFF_X = 21, OFF_Y = 114, FULL_W = 1056;

const NONE = "#e9eef5";
const TREND = {
  improving: { Icon: TrendingDown, color: "#16a34a", label: "IMPROVING" },
  worsening: { Icon: TrendingUp, color: "#d90429", label: "WORSENING" },
  steady: { Icon: Minus, color: "#5b6675", label: "STEADY" },
};

function StackedBar({ none, cats }) {
  const segs = [{ pct: none, color: NONE }, ...cats.map((c) => ({ pct: c.pct, color: c.color }))];
  const total = segs.reduce((s, x) => s + x.pct, 0) || 100;
  return (
    <div className="flex h-[30px] w-full overflow-hidden rounded-full bg-[#e6ebf2]" data-testid="drought-bar">
      {segs.map((s, i) => (s.pct > 0 ? <div key={i} style={{ width: `${(s.pct / total) * 100}%`, background: s.color }} /> : null))}
    </div>
  );
}

export function DroughtSlide({ drought }) {
  const cats = drought?.categories || [];
  if (!cats.length) return null;
  const worst = drought.worst;
  const t = TREND[drought.trend] || TREND.steady;
  const dsci = drought.dsci || {};
  return (
    <div data-testid="drought-slide" className="relative h-full w-full overflow-hidden" style={{ background: "linear-gradient(135deg, #f4f6fa 0%, #dfe6ef 100%)" }}>
      <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "repeating-linear-gradient(135deg, #000 0 2px, transparent 2px 26px)" }} />
      <div className="relative grid h-full w-full grid-cols-[52%_1fr] gap-6 px-[48px] py-[20px]">
        {/* official map, cropped to Georgia */}
        <div className="flex items-center justify-center rounded-[8px] bg-white p-4 shadow-[0_18px_40px_rgba(10,15,28,0.16)]">
          <div className="relative overflow-hidden" style={{ aspectRatio: `${MAP_W} / ${MAP_H}`, height: "100%", maxWidth: "100%" }}>
            <img
              src={drought.map_url}
              alt="Georgia Drought Monitor map"
              data-testid="drought-map"
              className="absolute top-0 left-0"
              style={{ width: `${(FULL_W / MAP_W) * 100}%`, maxWidth: "none", left: `${(-OFF_X / MAP_W) * 100}%`, top: `${(-OFF_Y / MAP_H) * 100}%` }}
            />
          </div>
        </div>

        {/* stats panel */}
        <div className="flex flex-col justify-center rounded-[8px] bg-white px-8 py-6 shadow-[0_18px_40px_rgba(10,15,28,0.16)]">
          <div className="flex items-center gap-2 font-oswald text-[21px] font-bold uppercase tracking-[0.28em] text-[#5b6675]">
            <Droplets className="h-6 w-6 text-[#1f63c9]" /> Georgia in Drought (D0–D4)
          </div>
          <div className="flex items-end gap-4">
            <div className="font-oswald text-[112px] font-black leading-[0.82] text-[#0a0f1c]" data-testid="drought-in-drought">{Math.round(drought.in_drought)}%</div>
            {worst ? (
              <div className="mb-3 flex flex-col gap-1">
                <span className="rounded-[5px] px-4 py-1.5 font-oswald text-[26px] font-black uppercase leading-none" style={{ background: worst.color, color: ["D2", "D3", "D4"].includes(worst.id) ? "#fff" : "#0a0f1c" }} data-testid="drought-worst">{worst.id} · {worst.label}</span>
                <span className="font-oswald text-[18px] font-bold uppercase tracking-wider text-[#5b6675]">Most intense level statewide</span>
              </div>
            ) : null}
          </div>

          <div className="mt-4"><StackedBar none={drought.none_pct} cats={cats} /></div>

          <div className="mt-4 grid grid-cols-1 gap-1.5">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center justify-between font-oswald text-[24px] font-semibold text-[#0a0f1c]" data-testid={`drought-cat-${c.id.toLowerCase()}`}>
                <span className="flex items-center gap-3">
                  <span className="inline-block h-[20px] w-[26px] rounded-[3px] border border-[#c8ced6]" style={{ background: c.color }} />
                  {c.id} <span className="text-[#5b6675]">{c.label}</span>
                </span>
                <span className={c.pct > 0 ? "" : "text-[#9ca3af]"}>{c.pct.toFixed(1)}%</span>
              </div>
            ))}
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-[#e2e8f0] pt-4">
            <div className="font-oswald text-[26px] font-bold text-[#0a0f1c]">DSCI {dsci.value ?? "—"}</div>
            <div className="flex items-center gap-2 font-oswald text-[22px] font-black uppercase tracking-wider" style={{ color: t.color }} data-testid="drought-trend">
              <t.Icon className="h-6 w-6" /> {t.label}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
